import React from "react"
import { Award, Clock, Lock, Shield, Star, TrendingUp, Users } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import type { StatsProps, TestimonialProps, TrustBadgeProps } from "@/lib/types"

// Sample data - in a real app, this would come from props or API
const stats: StatsProps[] = [
  { value: "10,000+", label: "Active Users", icon: Users },
  { value: "2.5M+", label: "Hours Saved", icon: Clock },
  { value: "80%", label: "Time Reduction", icon: TrendingUp },
  { value: "99.9%", label: "Uptime", icon: Shield }
]

const testimonials: TestimonialProps[] = [
  {
    quote: "VEDAVIVI saved me 3 hours every day. I finally have time for what matters most.",
    author: "Sarah Chen",
    role: "Marketing Director",
    company: "TechCorp"
  },
  {
    quote: "The email automation alone pays for itself. Game-changer for busy executives.",
    author: "Michael Rodriguez",
    role: "CEO",
    company: "StartupXYZ"
  },
  {
    quote: "I've reclaimed my evenings and weekends. The AI truly understands my priorities.",
    author: "Emily Johnson",
    role: "Product Manager",
    company: "InnovateCo"
  }
]

const trustBadges: TrustBadgeProps[] = [
  { label: "SOC2 Compliant", icon: Lock, description: "Enterprise-grade security" },
  { label: "GDPR Ready", icon: Shield, description: "Privacy by design" },
  { label: "4.9/5 Rating", icon: Star, description: "Trusted by users" },
  { label: "Award Winner", icon: Award, description: "Industry recognition" }
]

// Stats Grid Component
interface StatsGridProps {
  stats: StatsProps[]
}

const StatsGrid: React.FC<StatsGridProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-16">
      {stats.map((stat) => (
        <div key={stat.label} className="text-center">
          <div className="flex justify-center mb-3">
            {React.createElement(stat.icon, { 
              className: "h-8 w-8 md:h-10 md:w-10 text-brand-teal",
              "aria-hidden": "true" 
            })}
          </div>
          <div className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-1">
            {stat.value}
          </div>
          <div className="text-sm md:text-base text-slate-600 dark:text-slate-400">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  )
}

// Testimonial Card Component
interface TestimonialCardProps {
  testimonial: TestimonialProps
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial }) => {
  return (
    <Card className="h-full bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-shadow duration-300">
      <CardContent className="p-6">
        <div className="flex items-start mb-4">
          <div className="text-brand-teal text-4xl leading-none mr-2">"</div>
          <p className="text-slate-700 dark:text-slate-300 text-sm md:text-base leading-relaxed">
            {testimonial.quote}
          </p>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
          <div className="font-semibold text-slate-900 dark:text-white text-sm">
            {testimonial.author}
          </div>
          <div className="text-slate-600 dark:text-slate-400 text-xs">
            {testimonial.role}
            {testimonial.company && (
              <span className="text-brand-teal"> • {testimonial.company}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Testimonials Carousel Component
interface TestimonialCarouselProps {
  testimonials: TestimonialProps[]
}

const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({ testimonials }) => {
  return (
    <div className="mb-16">
      <h3 className="text-2xl md:text-3xl font-bold text-center text-slate-900 dark:text-white mb-8">
        What Our Users Say
      </h3>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {testimonials.map((testimonial) => (
          <TestimonialCard key={`${testimonial.author}-${testimonial.quote.substring(0, 20)}`} testimonial={testimonial} />
        ))}
      </div>
    </div>
  )
}

// Trust Badges Component
interface TrustBadgesProps {
  badges: TrustBadgeProps[]
}

const TrustBadges: React.FC<TrustBadgesProps> = ({ badges }) => {
  return (
    <div className="text-center">
      <h3 className="text-xl md:text-2xl font-semibold text-slate-900 dark:text-white mb-6">
        Trusted & Secure
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {badges.map((badge) => (
          <div key={badge.label} className="flex flex-col items-center p-4 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors duration-200">
            <div className="flex justify-center mb-2">
              {React.createElement(badge.icon, { 
                className: "h-6 w-6 text-brand-teal",
                "aria-hidden": "true" 
              })}
            </div>
            <div className="text-sm font-medium text-slate-900 dark:text-white mb-1">
              {badge.label}
            </div>
            {badge.description && (
              <div className="text-xs text-slate-600 dark:text-slate-400 text-center">
                {badge.description}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Main Social Proof Section Component
export interface SocialProofSectionProps {
  customStats?: StatsProps[]
  customTestimonials?: TestimonialProps[]
  customTrustBadges?: TrustBadgeProps[]
  className?: string
}

export const SocialProofSection: React.FC<SocialProofSectionProps> = ({
  customStats = stats,
  customTestimonials = testimonials,
  customTrustBadges = trustBadges,
  className = ""
}) => {
  return (
    <section 
      className={`py-16 md:py-24 bg-slate-50 dark:bg-slate-900 relative z-10 ${className}`}
      data-testid="social-proof-section"
    >
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Join Thousands Who've Reclaimed Their Time
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            See why professionals worldwide trust VEDAVIVI to automate their daily tasks and focus on what truly matters.
          </p>
        </div>

        {/* Stats Grid */}
        <StatsGrid stats={customStats} />

        {/* Testimonials */}
        <TestimonialCarousel testimonials={customTestimonials} />

        {/* Trust Badges */}
        <TrustBadges badges={customTrustBadges} />
      </div>
    </section>
  )
}

export default SocialProofSection
